/**
 * DashboardSidebar — searchable, filterable project list with alarm badges.
 *
 * Stateless wrt the data (parent owns projects + alarmCounts + filters).
 * Emits hoverChange, selectProject, requestSetLocation, and — in the
 * platform-admin grouped view — selectClient and editProject.
 *
 * Two shapes:
 *   - flat (org users): one ticket per project, as before.
 *   - grouped (platform admin, `groupByClient`): one row per client; the
 *     selected client's project tickets open in a second panel docked to
 *     the right of this one (`.dashboard-sidebar--secondary`).
 */

import type { SiteListItem, UpdateProjectInput, AdminOrganizationItem } from '../../services/projectApi';
import type { AlarmCount } from './DashboardMap';
import type { ClientGroup } from './clientGroups';
import DashboardProjectItem from './DashboardProjectItem';
import './DashboardSidebar.css';

export type FilterKey = 'all' | 'alarms' | 'recent';

/** The client whose panel is open. Carries the name so the panel header
 *  survives a search that drops every project of that client. */
export interface SelectedClient {
  id: string;
  name: string;
}

interface DashboardSidebarProps {
  projects: SiteListItem[];
  alarmCounts: Record<string, AlarmCount>;
  search: string;
  onSearchChange: (s: string) => void;
  filter: FilterKey;
  onFilterChange: (f: FilterKey) => void;
  hoveredId: string | null;
  onHoverChange: (id: string | null) => void;
  onSelectProject: (site: SiteListItem) => void;
  onRequestSetLocation: (site: SiteListItem) => void;
  /** Present only for callers allowed to edit site details (platform admin).
   *  `patch` = changed fields only; null clears location/capacity. */
  onEditProject?: (site: SiteListItem, patch: UpdateProjectInput) => Promise<boolean>;
  /** Platform admin: move a site to another organization (typed-name confirm). */
  onMoveProject?: (site: SiteListItem, targetOrgId: string, confirmName: string) => Promise<boolean>;
  /** Target organizations for the move panel; null = not loaded yet. */
  adminOrgs?: AdminOrganizationItem[] | null;
  /** Grouped (client-first) view. `clientGroups` must be derived from the
   *  same `projects` the flat view would show. */
  groupByClient?: boolean;
  clientGroups?: ClientGroup[];
  selectedClient?: SelectedClient | null;
  onSelectClient?: (client: SelectedClient | null) => void;
  loading: boolean;
  loadingProjectId: string | null;
  totalCount: number;
  onNewProject: () => void;
  onOpenOrgSettings: () => void;
  onOpenTeam: () => void;
  onOpenAdmin: () => void;
  isAdmin: boolean;
  hasOrg: boolean;
  onLogout: () => void;
  userEmail?: string;
}

export default function DashboardSidebar({
  projects,
  alarmCounts,
  search,
  onSearchChange,
  filter,
  onFilterChange,
  hoveredId,
  onHoverChange,
  onSelectProject,
  onRequestSetLocation,
  onEditProject,
  onMoveProject,
  adminOrgs = null,
  groupByClient = false,
  clientGroups = [],
  selectedClient = null,
  onSelectClient,
  loading,
  loadingProjectId,
  totalCount,
  onNewProject,
  onOpenOrgSettings,
  onOpenTeam,
  onOpenAdmin,
  isAdmin,
  hasOrg,
  onLogout,
  userEmail,
}: DashboardSidebarProps) {
  const selectedGroup =
    groupByClient && selectedClient
      ? clientGroups.find((g) => g.id === selectedClient.id) ?? null
      : null;
  const listIsEmpty = groupByClient ? clientGroups.length === 0 : projects.length === 0;
  const hasSearch = search.trim().length > 0;

  const renderProject = (site: SiteListItem, showClient: boolean) => (
    <DashboardProjectItem
      key={site.id}
      site={site}
      counts={alarmCounts[site.id]}
      isHovered={hoveredId === site.id}
      isLoading={loadingProjectId === site.id}
      showClient={showClient}
      onHoverChange={onHoverChange}
      onSelect={onSelectProject}
      onRequestSetLocation={onRequestSetLocation}
      onEdit={onEditProject}
      onMove={onMoveProject}
      orgs={adminOrgs}
    />
  );

  const mainEmptyMessage = () => {
    if (filter === 'alarms') return 'No projects with active alarms';
    if (filter === 'recent') return 'No recently updated projects';
    if (hasSearch) return 'No projects match your search';
    return (
      <div className="dashboard-sidebar__empty-onboard">
        <span>No projects yet.</span>
        <button
          className="dashboard-sidebar__empty-cta"
          onClick={onNewProject}
          type="button"
        >
          + Create your first project
        </button>
      </div>
    );
  };

  const clientEmptyMessage = () => {
    if (filter === 'alarms') return 'No projects with active alarms for this client';
    if (filter === 'recent') return 'No recently updated projects for this client';
    if (hasSearch) return 'No projects match your search for this client';
    return 'No projects for this client';
  };

  return (
    <>
      <aside className="dashboard-sidebar" aria-label={groupByClient ? 'Clients' : 'Projects'}>
        <header className="dashboard-sidebar__header">
          <h1 className="dashboard-sidebar__title">{groupByClient ? 'Clients' : 'Projects'}</h1>
          <span className="dashboard-sidebar__count">{groupByClient ? clientGroups.length : totalCount}</span>
          <div className="dashboard-sidebar__header-actions">
            {hasOrg && (
              <>
                <button
                  className="dashboard-sidebar__icon-btn"
                  onClick={onOpenOrgSettings}
                  title="Organization settings"
                  aria-label="Organization settings"
                  type="button"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>
                <button
                  className="dashboard-sidebar__icon-btn"
                  onClick={onOpenTeam}
                  title="Team"
                  aria-label="Team"
                  type="button"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </button>
              </>
            )}
            {isAdmin && (
              <button
                className="dashboard-sidebar__icon-btn"
                onClick={onOpenAdmin}
                title="Admin dashboard"
                aria-label="Admin dashboard"
                type="button"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </button>
            )}
            <button
              className="dashboard-sidebar__logout"
              onClick={onLogout}
              title={userEmail ? `Sign out (${userEmail})` : 'Sign out'}
              aria-label="Sign out"
              type="button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </header>

        <div className="dashboard-sidebar__new-project-wrap">
          <button
            className="dashboard-sidebar__new-project"
            onClick={onNewProject}
            type="button"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New project
          </button>
        </div>

        <div className="dashboard-sidebar__search">
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search projects..."
            className="dashboard-sidebar__search-input"
          />
        </div>

        <div className="dashboard-sidebar__filters" role="tablist">
          {(['all', 'alarms', 'recent'] as const).map((k) => (
            <button
              key={k}
              role="tab"
              aria-selected={filter === k}
              onClick={() => onFilterChange(k)}
              className={`dashboard-sidebar__chip ${filter === k ? 'is-active' : ''}`}
            >
              {k === 'all' ? 'All' : k === 'alarms' ? 'Alarms' : 'Recent'}
            </button>
          ))}
        </div>

        <ul className="dashboard-sidebar__list">
          {loading && listIsEmpty && (
            <li className="dashboard-sidebar__empty">Loading projects…</li>
          )}
          {!loading && listIsEmpty && (
            <li className="dashboard-sidebar__empty">{mainEmptyMessage()}</li>
          )}
          {groupByClient
            ? clientGroups.map((group) => {
                const isSelected = selectedClient?.id === group.id;
                const isCritical = group.critical > 0;
                const isWarning = !isCritical && group.warning > 0;
                const n = group.projects.length;
                const meta =
                  `${n} ${n === 1 ? 'project' : 'projects'}` +
                  (group.unpinned > 0 ? ` · ${group.unpinned} without location` : '');
                return (
                  <li
                    key={group.id}
                    className={`dashboard-sidebar__item dashboard-sidebar__client ${isSelected ? 'is-selected' : ''}`}
                  >
                    <button
                      className="dashboard-sidebar__item-main"
                      onClick={() =>
                        onSelectClient?.(isSelected ? null : { id: group.id, name: group.name })
                      }
                      aria-expanded={isSelected}
                    >
                      <div className="dashboard-sidebar__item-row">
                        <span className="dashboard-sidebar__item-name">{group.name}</span>
                        {isCritical && (
                          <span className="dashboard-sidebar__badge dashboard-sidebar__badge--critical" title={`${group.critical} critical · ${group.warning} warning`}>
                            {group.critical + group.warning}
                          </span>
                        )}
                        {isWarning && (
                          <span className="dashboard-sidebar__badge dashboard-sidebar__badge--warning" title={`${group.warning} warning`}>
                            {group.warning}
                          </span>
                        )}
                        <svg className="dashboard-sidebar__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </div>
                      <div className="dashboard-sidebar__item-meta">{meta}</div>
                    </button>
                  </li>
                );
              })
            : projects.map((site) => renderProject(site, true))}
        </ul>
      </aside>

      {groupByClient && selectedClient && (
        <aside
          className="dashboard-sidebar dashboard-sidebar--secondary"
          aria-label={`${selectedClient.name} projects`}
        >
          <header className="dashboard-sidebar__header">
            <h2 className="dashboard-sidebar__title dashboard-sidebar__title--client" title={selectedClient.name}>
              {selectedClient.name}
            </h2>
            <span className="dashboard-sidebar__count">{selectedGroup?.projects.length ?? 0}</span>
            <div className="dashboard-sidebar__header-actions">
              <button
                className="dashboard-sidebar__icon-btn"
                onClick={() => onSelectClient?.(null)}
                title="Close"
                aria-label="Close client panel"
                type="button"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </header>

          <ul className="dashboard-sidebar__list">
            {(!selectedGroup || selectedGroup.projects.length === 0) && (
              <li className="dashboard-sidebar__empty">{clientEmptyMessage()}</li>
            )}
            {selectedGroup?.projects.map((site) => renderProject(site, false))}
          </ul>
        </aside>
      )}
    </>
  );
}
